import { useMemo, useState, useEffect } from 'react'
import { toast } from 'sonner'
import { Repeat, CalendarClock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { useRecurringRules, useIncludeRecurring, useRecurringSkips, type RecurringOccurrence } from '@/hooks/useRecurring'
import { useTransactions } from '@/hooks/useTransactions'
import { formatCurrency } from '@/lib/currency'
import { accountIcon } from '@/lib/accountIcon'
import { monthOccurrenceStatus } from '@/lib/recurring'
import { norm } from '@/lib/text'
import { useT } from '@/i18n/I18nProvider'

function monthBounds(year: number, month0: number) {
  const iso = (d: Date) => d.toISOString().slice(0, 10)
  return { from: iso(new Date(year, month0, 1)), to: iso(new Date(year, month0 + 1, 0)) }
}
function fmtBr(isoDate: string) {
  const [y, m, d] = isoDate.split('-')
  return `${d}/${m}/${y}`
}

// Diálogo "Incluir recorrente": lista TODAS as recorrentes que caem no mês e ainda
// NÃO foram incluídas (igual às transações). Cada uma é lançada na SUA própria conta.
// `accountId` (a conta de onde o diálogo foi aberto) só ordena as dela no topo.
export function IncludeRecurringDialog({
  open,
  onOpenChange,
  year,
  month0,
  accountId,
  title,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  year: number
  month0: number
  accountId?: string
  title?: string
}) {
  const { t } = useT()
  const { data: rules } = useRecurringRules()
  const range = useMemo(() => monthBounds(year, month0), [year, month0])
  const { data: monthTxs } = useTransactions(range)
  const { data: skips } = useRecurringSkips(year, month0)
  const include = useIncludeRecurring()
  const [q, setQ] = useState('')

  const available = useMemo(() => {
    const items: (RecurringOccurrence & { accountName: string; key: string; ownAccount: boolean })[] = []
    for (const r of rules ?? []) {
      // Casa por MÊS (conta + descrição/recurring_id), pareando 1:1 — cobre as
      // ocorrências importadas do LM (recurring_id nulo) lançadas em outro dia.
      for (const o of monthOccurrenceStatus(r, monthTxs ?? [], year, month0)) {
        if (!o.included && !skips?.has(`${r.id}|${o.date}`)) // não oferece a marcada N/A nessa ocorrência
          items.push({
            ruleId: r.id,
            date: o.date,
            template: r.template,
            accountName: r.account ? `${accountIcon(r.account)} ${r.account.name}` : '—',
            ownAccount: !!accountId && r.template.account_id === accountId,
            key: r.id + '|' + o.date,
          })
      }
    }
    // As da conta de onde o diálogo foi aberto primeiro; depois por data.
    return items.sort(
      (a, b) => Number(b.ownAccount) - Number(a.ownAccount) || a.date.localeCompare(b.date),
    )
  }, [rules, monthTxs, skips, accountId, year, month0])

  // Busca por descrição ou conta.
  const filtered = useMemo(() => {
    const nq = norm(q)
    if (!nq) return available
    return available.filter(
      (i) => norm(i.template.description ?? '').includes(nq) || norm(i.accountName).includes(nq),
    )
  }, [available, q])

  const [sel, setSel] = useState<Set<string>>(new Set())
  useEffect(() => {
    if (open) {
      setSel(new Set())
      setQ('')
    }
  }, [open])

  function toggle(key: string) {
    setSel((s) => {
      const n = new Set(s)
      n.has(key) ? n.delete(key) : n.add(key)
      return n
    })
  }
  const allSelected = filtered.length > 0 && filtered.every((i) => sel.has(i.key))

  async function add() {
    const chosen = available.filter((i) => sel.has(i.key))
    if (chosen.length === 0) return
    try {
      await include.mutateAsync(chosen.map(({ ruleId, date, template }) => ({ ruleId, date, template })))
      toast.success(t('rec.includeDialog.addedN', { count: chosen.length }))
      onOpenChange(false)
    } catch (e) {
      toast.error(t('rec.toast.includeError'), { description: (e as Error).message })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90svh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title ?? t('rec.includeDialog.title')}</DialogTitle>
          <DialogDescription>{t('rec.includeDialog.description')}</DialogDescription>
        </DialogHeader>

        <div className="py-2">
          {available.length === 0 ? (
            <p className="text-muted-foreground p-6 text-center text-sm">
              {t('rec.includeDialog.empty')}
            </p>
          ) : (
            <>
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t('rec.includeDialog.searchPlaceholder')}
                className="mb-2 h-9"
              />
              <label className="text-muted-foreground mb-2 flex items-center gap-2 text-xs">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={(v) =>
                    setSel((s) => {
                      const n = new Set(s)
                      for (const i of filtered) (v === true ? n.add(i.key) : n.delete(i.key))
                      return n
                    })
                  }
                />
                {t('rec.includeDialog.selectAll', { count: filtered.length })}
              </label>
              {filtered.length === 0 ? (
                <p className="text-muted-foreground p-4 text-center text-sm">{t('rec.includeDialog.noResults')}</p>
              ) : (
              <div className="divide-border divide-y rounded-lg border">
                {filtered.map((it) => {
                  const isExpense = it.template.kind === 'expense'
                  return (
                    <label key={it.key} className="hover:bg-accent/40 flex cursor-pointer items-center gap-3 p-3">
                      <Checkbox checked={sel.has(it.key)} onCheckedChange={() => toggle(it.key)} />
                      <Repeat className="text-primary size-4 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium">
                          {it.template.description || t('rec.noDescription')}
                        </div>
                        <div className="text-muted-foreground flex items-center gap-1 text-xs">
                          <CalendarClock className="size-3" /> {fmtBr(it.date)} · {it.accountName}
                        </div>
                      </div>
                      <div className={`tabular-nums text-sm font-medium ${isExpense ? '' : 'text-success'}`}>
                        {isExpense ? '-' : '+'}
                        {formatCurrency(it.template.amount, it.template.currency)}
                      </div>
                    </label>
                  )
                })}
              </div>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('rec.includeDialog.close')}
          </Button>
          <Button onClick={add} disabled={sel.size === 0 || include.isPending}>
            {include.isPending
              ? t('rec.includeDialog.including')
              : sel.size > 0
                ? t('rec.includeDialog.includeCount', { count: sel.size })
                : t('rec.include')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
